import React from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from '../common/Markdown';
import WorkoutEnrichmentBlocks from './WorkoutEnrichmentBlocks';
import { HiCheck, HiXMark, HiPencilSquare, HiArrowsRightLeft, HiPlus, HiArrowRight, HiFlag, HiSparkles } from 'react-icons/hi2';

export default function WorkoutDetailModal({
  isOpen,
  onClose,
  workout,
  colorClass,
  isToday,
  isCompleted,
  isMissed,
  actualStats,
  onAddAnotherWorkout,
  onMarkComplete,
  onMarkMissed,
  onAskCoachForMissed,
  onEdit,
  onMove,
  onViewDetails,
  schemaVersion = 1,
  eventTitle = null
}) {
  const { t } = useTranslation();

  if (!isOpen || !workout) return null;

  const focus = workout?.workout_focus ? String(workout.workout_focus).replaceAll('_', ' ') : '';
  const typeLabelBase = workout.type ? workout.type.replaceAll('_', ' ') : 'workout';
  const typeLabel = focus ? `${typeLabelBase} — ${focus}` : typeLabelBase;

  const plannedKm = workout.distance_km ?? null;
  const plannedMin = workout.duration_min ?? null;
  const plannedElev = workout.altitude_gain_m ?? null;

  return (
    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-4 z-50 animate-fade-in">
      <div className="bg-white w-full md:max-w-2xl rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] md:max-h-[90vh] flex flex-col border border-gray-100 animate-slide-in-bottom md:animate-none">
        
        {/* Header */}
        <div className="p-6 border-b border-gray-100 flex justify-between items-start gap-4 bg-white/95 backdrop-blur-md sticky top-0 z-10">
          <div className="min-w-0">
            <div className="flex items-center flex-wrap gap-2 mb-2">
              <div className={`w-2.5 h-2.5 rounded-full ${colorClass || 'bg-gray-300'}`} />
              <div className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                {typeLabel}
              </div>
              {isToday && (
                <span className="text-[9px] font-black uppercase bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full border border-indigo-100">
                  {t('plan.today')}
                </span>
              )}
              {isCompleted && (
                <span className="text-[9px] font-black uppercase bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-100 flex items-center gap-1">
                  <HiCheck className="w-3 h-3" /> {t('plan.completed')}
                </span>
              )}
              {isMissed && !isCompleted && (
                <span className="text-[9px] font-black uppercase bg-red-50 text-red-600 px-2 py-0.5 rounded-full border border-red-100 flex items-center gap-1">
                  <HiXMark className="w-3 h-3" /> {t('plan.missed')}
                </span>
              )}
              {workout?.event_id ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-600 text-white uppercase flex items-center gap-1 max-w-[180px]">
                  <HiFlag className="w-3 h-3 shrink-0" /> <span className="truncate">{eventTitle || t('events.event_day')}</span>
                </span>
              ) : workout?.prepared_for_event_id && eventTitle ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-indigo-50 text-indigo-700 border border-indigo-100 uppercase flex items-center gap-1 max-w-[200px]">
                  <HiFlag className="w-3 h-3 shrink-0" /> <span className="truncate">{t('events.prepared_for', { title: eventTitle })}</span>
                </span>
              ) : null}
              {workout?.added_by === 'coach' ? (
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-white text-indigo-600 border border-indigo-200 uppercase flex items-center gap-1">
                  <HiSparkles className="w-3 h-3" /> {t('events.added_by_coach')}
                </span>
              ) : null}
            </div>

            <h3 className="text-xl md:text-2xl font-black text-gray-900 leading-tight mb-1">
              {workout.title}
            </h3>
            <div className="text-xs font-bold text-gray-400 font-mono flex items-center gap-1.5">
              <span>{workout.day}</span>
              {workout.date && (
                <>
                  <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                  <span>{workout.date}</span>
                </>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors shrink-0"
            aria-label={t('workout.close')}
          >
            <HiXMark className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="p-6 overflow-y-auto">
          {/* Metrics Pills */}
          <div className="flex flex-wrap gap-2 mb-6">
            {plannedKm ? (
              <span className="px-3 py-1.5 rounded-xl bg-blue-50 text-blue-700 border border-blue-100 text-xs font-bold tabular-nums">
                {plannedKm} km
              </span>
            ) : null}
            {plannedMin ? (
              <span className="px-3 py-1.5 rounded-xl bg-green-50 text-green-700 border border-green-100 text-xs font-bold tabular-nums">
                {plannedMin} min
              </span>
            ) : null}
            {plannedElev ? (
              <span className="px-3 py-1.5 rounded-xl bg-yellow-50 text-yellow-700 border border-yellow-100 text-xs font-bold tabular-nums">
                +{plannedElev} m
              </span>
            ) : null}
          </div>

          {/* Description Box */}
          <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 mb-6">
            <Markdown className="text-sm text-gray-700 leading-relaxed [&_p]:my-2 [&_strong]:text-gray-900 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1">
              {workout.description}
            </Markdown>
          </div>

          <WorkoutEnrichmentBlocks workout={workout} className="mt-4" />

          {/* Skeleton Loader */}
          {!workout.why && !workout.fueling && !workout.technique && (schemaVersion >= 2 && !workout.is_unplanned_slot) && (
            <div className="space-y-3 animate-pulse mt-4">
              <div className="h-4 bg-gray-100 rounded w-3/4"></div>
              <div className="h-4 bg-gray-100 rounded w-1/2"></div>
              <div className="h-4 bg-gray-100 rounded w-5/6"></div>
            </div>
          )}

          {/* Actual Stats (if completed) */}
          {actualStats ? (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <div className="text-[10px] font-black uppercase tracking-wider text-gray-400 mb-3">
                {t('plan.selected_workout_panel.actual')}
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-2xl border border-gray-100 bg-white shadow-sm text-center">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{t('plan.distance')}</div>
                  <div className="text-lg font-black text-gray-900 tabular-nums">{Math.round(actualStats.km || 0)} <span className="text-xs font-bold text-gray-400">km</span></div>
                </div>
                <div className="p-3 rounded-2xl border border-gray-100 bg-white shadow-sm text-center">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{t('plan.duration')}</div>
                  <div className="text-lg font-black text-gray-900 tabular-nums">{Math.round(actualStats.min || 0)} <span className="text-xs font-bold text-gray-400">min</span></div>
                </div>
                <div className="p-3 rounded-2xl border border-gray-100 bg-white shadow-sm text-center">
                  <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-1">{t('plan.elevation')}</div>
                  <div className="text-lg font-black text-gray-900 tabular-nums">+{Math.round(actualStats.elev || 0)} <span className="text-xs font-bold text-gray-400">m</span></div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Action Buttons Footer */}
        <div className="p-4 md:p-6 border-t border-gray-100 bg-gray-50/50 flex flex-col gap-3 sticky bottom-0 z-10">
          
          {/* Primary Actions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {onMarkComplete && !isCompleted && !isMissed && (
              <button
                type="button"
                onClick={onMarkComplete}
                className="w-full py-3.5 px-4 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 text-sm uppercase tracking-wide"
              >
                <HiCheck className="w-5 h-5" /> {t('plan.mark_complete')}
              </button>
            )}

            {isCompleted && onViewDetails && (
              <button
                type="button"
                onClick={onViewDetails}
                className="w-full py-3.5 px-4 rounded-full font-bold bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-2 text-sm uppercase tracking-wide md:col-span-2"
              >
                {t('workout.view_details')} <HiArrowRight className="w-5 h-5" />
              </button>
            )}

            {typeof onAddAnotherWorkout === 'function' && (
              <button
                type="button"
                onClick={onAddAnotherWorkout}
                className={`w-full py-3.5 px-4 rounded-full font-bold transition-all duration-200 flex items-center justify-center gap-2 text-sm uppercase tracking-wide ${
                  !onMarkComplete || isCompleted || isMissed
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-500/30 hover:shadow-indigo-500/40 hover:-translate-y-0.5'
                    : 'bg-white border-2 border-indigo-100 text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50'
                }`}
              >
                <HiPlus className="w-5 h-5" /> {t('plan.selected_workout_panel.add_another')}
              </button>
            )}
          </div>

          {/* Secondary Actions Row */}
          {(!isCompleted && !isMissed) && (
            <div className="grid grid-cols-2 gap-2 mt-1">
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="py-2.5 px-2 rounded-xl font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors text-xs flex items-center justify-center gap-1.5"
                >
                  <HiPencilSquare className="w-4 h-4" /> {t('workout.edit_workout')}
                </button>
              )}

              {onMove && (
                <button
                  type="button"
                  onClick={onMove}
                  className="py-2.5 px-2 rounded-xl font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800 transition-colors text-xs flex items-center justify-center gap-1.5"
                >
                  <HiArrowsRightLeft className="w-4 h-4" /> {t('plan.move.button')}
                </button>
              )}

              {onMarkMissed && (
                <button
                  type="button"
                  onClick={onMarkMissed}
                  className="py-2.5 px-2 rounded-xl font-bold bg-red-50 text-red-600 hover:bg-red-100 transition-colors text-xs flex items-center justify-center gap-1.5"
                >
                  <HiXMark className="w-4 h-4" /> {t('plan.missed_no_change')}
                </button>
              )}

              {onAskCoachForMissed && (
                <button
                  type="button"
                  onClick={onAskCoachForMissed}
                  className="py-2.5 px-2 rounded-xl font-bold bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors text-xs flex items-center justify-center gap-1.5"
                >
                  <HiArrowRight className="w-4 h-4" /> {t('plan.ask_coach_short')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


